import React, { useEffect, useMemo, useRef, useState } from "react";
import { Scatter } from "react-chartjs-2";
import "chart.js/auto";
import FA from "../../fa";
import {
  STATUS,
  TONES,
  QUADRANT_TONE,
  INK,
  MUTED,
  GRID,
  CONTEXT,
  CARD_BORDER,
  nestBg,
} from "../tokens";
import {
  OUTLIER_SIGMA,
  MIN_OUTLIER_N,
  COHORT_PEER_TOLERANCE,
  clamp,
  cycleBandsForSeries,
} from "../model";
import { chartOptions, axisTitle } from "../charts";
import { byName, isGradeRow, isUnplacedGrade } from "../format";
import { Sparkline, PatternBar, DeltaChip, StatusPill } from "../primitives";
import {
  KINDS, kindsPresent, fallbacksPresent, FALLBACK_HELP, rowTone, CASCADE_ORDER,
} from "../status";
import {
  CARD_PAD,
  Card,
  Table,
  HeadRow,
  Th,
  SortTh,
  Td,
  Row,
  Caret,
  GhostButton,
  SectionHead,
  PlotNote,
  Select,
  TableNote,
  DEFAULT_ROWS,
  useElementWidth,
} from "../ui";

// Every mark that stands for a ROW takes its colour from here — the row's verdict
// tone, the same one its pill wears (see rowTone in status.js). `STATUS` is no
// longer a row palette at all: it survives only as the quadrant REGION captions,
// which label areas of the plot rather than any school in it.
const markColor = (row) => (TONES[rowTone(row)] || TONES.attention).color;

// Bubble radius = roster. Shared by the dataset and by the plugin's raised disc,
// which has to be exactly the same size as the point it sits under or the shadow
// reads as a halo.
const dotRadius = (row) => 6 + Math.sqrt(row.roster) * 0.7;

const WORKLIST_COLS = 6;
const COHORT_COLS = 6;

// How a row's robot count is rendered, in the one place both tables read it.
// `null` means robots aren't a property of this kind of row (a grade cohort, an
// unowned classroom) and prints an em dash; `0` means this row could have robots
// and has none, which is the finding the column exists for and prints as a zero.
const robotCount = (cap) => (cap ? cap.total : null);
const robotTitle = (cap, word) => {
  if (!cap) return `Robots are not a property of a ${word}.`;
  if (!cap.total) return "No robot is assigned to this row.";
  const dark = cap.total - cap.working;
  return (
    `${cap.total} robot${cap.total === 1 ? "" : "s"} assigned` +
    (dark
      ? `, ${cap.working} of them delivering lessons in this window ` +
        `(${dark} dark). Capacity counts the working ones.`
      : ", all delivering lessons in this window.")
  );
};

// Shared column geometry for the worklist and every nested cohort panel — the
// two tables are the same measures at different depths, so they get the same
// widths and the numbers stay in vertical register as you drill down. The caret
// column is added separately by whichever table has one.
//
// A function rather than a constant element because only the TOP-level worklist
// is sortable: a drill panel holds the handful of cohorts inside one row, where
// sorting is noise and the widths still have to match the parent exactly. Pass
// `sort`/`onSort` to get sortable headers, omit them for plain ones — one
// definition of the widths either way.
const measureCols = (sort, onSort, robots = false) => {
  const H = ({ k, ...rest }) =>
    onSort ? (
      <SortTh
        sortKey={k}
        sort={sort}
        onSort={onSort}
        defaultDir={SORT_DIR[k]}
        {...rest}
      />
    ) : (
      <Th {...rest} />
    );
  return (
    <>
      <H k="roster" align="right" width={72}>Roster</H>
      {/* Beside Roster, because the pair is the reading: 42 learners and 0 robots
          is a different story from 42 learners and 2 robots, and neither number
          means much alone. Sortable, so "who has hardware and isn't using it"
          and "who has none" are each one click. */}
      {robots && (
        <H k="robots" align="right" width={72}>Robots</H>
      )}
      <H k="reach" align="right" width={88}>Reached</H>
      <H k="depth" align="right" width={96}>Std / learner</H>
      {/* Trend is two marks in one cell and neither is a single sortable
          quantity, so it stays a plain header in both tables. */}
      <Th width={112}>Trend</Th>
    </>
  );
};
const TABLE_MIN_WIDTH = 760;

// ---- geometry of the plot/table pair ---------------------------------------
//
// The scatter and the worklist are one object with two views of the same rows —
// the filters govern both, hovering either highlights the other, clicking a dot
// reveals its row. So they are laid out as one card, and the only questions are
// whether the two views sit side by side or stacked, and how big the plot gets.
//
// Side by side WHENEVER THERE IS ROOM, which is a fact about the width this pair
// received and not about the viewport (see useElementWidth). It needs the table
// at its full TABLE_MIN_WIDTH — every column below that is a fixed width summing
// to 730px, so there is nothing to give back — beside a plot still wide enough to
// read. Below that, stacked.

// One aspect ratio, everywhere. Filling the content column made this plot
// 831x266 — 3.1:1 — and the quantity the lens ranks on is distance from the
// crosshair in sigma, which is isotropic. At that ratio a unit 1σ out in reach is
// drawn three times farther from the centre than one 1σ out in depth, so the
// picture misstated the very measure the worklist beside it is ordered by. A
// near-square box cannot make the mapping exactly isotropic (the two sigmas
// differ per district) but it stops the distortion being structural.
const PLOT_ASPECT = 1.45;

// Stacked, the plot's height is what pushes the table down the page, so it is
// ceilinged — and because the aspect is fixed, ceilinging the height IS the width
// cap. This is the whole "the plot doesn't need to be that big" fix: a centred
// figure inside a full-width card rather than a band across it.
const PLOT_STACKED_MAX_H = 300;
const PLOT_STACKED_MAX_W = Math.round(PLOT_STACKED_MAX_H * PLOT_ASPECT);
// Never below this, or the axis labels collide before the dots do.
const PLOT_MIN_W = 240;

// Side by side there is nothing underneath the plot to push anywhere, so it is
// free to grow with the pair — but only to a share of it, and the smaller share.
// The table is the view with the names, the numbers and the drill-downs in it;
// the plot summarises visually what the table states precisely.
//
// These are CANVAS widths; the column adds its own padding on top, which is why
// the side-by-side threshold does too.
const PLOT_SHARE = 0.34;
const PLOT_COL_MIN_W = 400;
const PLOT_COL_MAX_W = 560;
const PAIR_MIN_WIDTH = TABLE_MIN_WIDTH + PLOT_COL_MIN_W + CARD_PAD * 2;
// A scrollbar is worth ~15px and switching arrangements changes the page's
// height, so a pair sitting within a scrollbar's width of the threshold could
// switch, lose the scrollbar, and switch back forever. Once side by side, it
// takes a little more than that to go back.
const PAIR_HYSTERESIS = 24;


// Status-kind legend. Rendered once above the worklist and listing only the kinds
// actually present, so a district with no capacity join never sees an entry it
// can't produce.
//
// This is the ONE place the new statuses spend words, and even here they spend
// only their names: the definitions ride as hover titles. Printing seven
// one-sentence definitions above the table would put a paragraph between the
// reader and the worklist — the same mistake as a clause per row, just relocated.
//
// `column` stacks it into a rail. Stacked over the table, the plot is a 435px
// figure centred in a card twice that wide, so a horizontal legend under it
// spends a whole line of the gap between plot and table to say what fits in the
// empty gutter beside it. Side by side there is no gutter — the plot column is
// only as wide as the canvas — so it stays a row there.
const KindLegend = ({ rows, column = false }) => {
  const present = kindsPresent(rows);
  // Rows no rule claimed keep the quadrant pill, and on real districts that is
  // about a third of them — so the legend has to name those words too or the
  // commonest pill on screen is the one it doesn't explain.
  const fallbacks = fallbacksPresent(rows);
  if (!present.length && !fallbacks.length) return null;
  const entries = [
    ...present.map((k) => ({
      key: k,
      label: KINDS[k].label,
      tone: KINDS[k].tone,
      help: KINDS[k].help,
    })),
    ...fallbacks.map((s) => ({
      key: `fallback-${s}`,
      label: STATUS[s].label,
      tone: QUADRANT_TONE[s],
      help: FALLBACK_HELP[s],
    })),
  ];
  return (
    <div
      style={{
        display: "flex",
        fontSize: 11.5,
        color: MUTED,
        ...(column
          ? {
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 6,
            }
          : {
              flexWrap: "wrap",
              gap: "4px 14px",
              alignItems: "center",
              margin: "0 0 10px",
            }),
      }}
    >
      <span>Status</span>
      {entries.map((e) => (
        <span
          key={e.key}
          title={e.help}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            cursor: "help",
            borderBottom: `1px dotted ${GRID}`,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: (TONES[e.tone] || TONES.attention).color,
              display: "inline-block",
            }}
          />
          <span style={{ color: INK, fontWeight: 600 }}>{e.label}</span>
        </span>
      ))}
    </div>
  );
};

// ---- worklist sort + filter -------------------------------------------------
// The label a row's Status cell shows. One function, because it is read in three
// places — the pill, the status filter's option list, and the status sort — and a
// filter that offered a word the pill doesn't print would be unusable.
const statusLabel = (row) =>
  row.kindLabel || (STATUS[row.status] && STATUS[row.status].label) || null;

// Comparators. `null` sort means the order the list arrived in, which is the
// σ-ranking — that stays the default because it is the answer to "where do I
// look first", and no column header can express it (σ lives inside the Status
// cell as a suffix, not as a column of its own).
//
// Status sorts by SEVERITY, not alphabetically: the tone rank first, then σ
// within a tone. Alphabetical status is a sort nobody wants — it would put
// "Bursty" above "Not started" — and severity is the reason someone clicks it.
const SORT_DIR = { name: "asc", roster: "desc", robots: "asc", reach: "desc", depth: "desc", status: "desc" };
// Robots defaults to FEWEST first, unlike every other measure here. The other
// columns answer "who is doing best"; this one is on screen to find the rows
// with none.
// How the active sort is described under the heading. Spelled out rather than left
// as "sorted by column": the arrow in the header says WHICH column, but only if
// you happen to look up there, and "reach, highest first" is the sentence that
// tells you why the row order changed.
const SORT_WORDS = {
  name: ["A–Z", "Z–A"],
  // Grade rows sort numerically (see byName), so "A–Z" would describe an order
  // the table isn't in. Keyed separately rather than switched inside `name`
  // because the pair is a property of the ROWS, not of the column.
  gradeName: ["lowest first", "highest first"],
  roster: ["smallest first", "largest first"],
  robots: ["fewest first", "most first"],
  reach: ["lowest first", "highest first"],
  depth: ["lowest first", "highest first"],
  status: ["best first", "worst first"],
};
const SORT_NOUNS = {
  roster: "roster",
  robots: "robot count",
  reach: "reach",
  depth: "standards per learner",
  status: "severity",
};
// `labels` overrides one measure's noun for a caller whose column means something
// else. The cohort tables need it for `status`: theirs is distance from one named
// peer, and calling that "severity" borrows the district cascade's word for a
// scale it isn't on — the same conflation PEER_STATUS exists to undo.
const sortPhrase = (sort, noun, rows = [], labels = {}) => {
  if (!sort || !SORT_WORDS[sort.key]) return null;
  const label =
    sort.key === "name"
      ? noun.one
      : labels[sort.key] || SORT_NOUNS[sort.key];
  const wordKey =
    sort.key === "name" && rows.length && rows.every(isGradeRow)
      ? "gradeName"
      : sort.key;
  return `sorted by ${label}, ${SORT_WORDS[wordKey][sort.dir === "asc" ? 0 : 1]}`;
};
const SORTERS = {
  // Surname-first for person rows, as-printed for everything else — see
  // format.js's nameSortKey for why the row decides and not the name.
  name: byName,
  roster: (a, b) => a.roster - b.roster,
  // A row robots aren't a property of sorts as -1, i.e. below a genuine zero.
  // Those rows have nothing to say on this column and shouldn't sit between the
  // rows that do.
  robots: (a, b) => {
    const n = (r) => (r.cap ? r.cap.total : -1);
    return n(a) - n(b);
  },
  reach: (a, b) => a.reach - b.reach,
  depth: (a, b) => a.depth - b.depth,
  status: (a, b) => {
    const rank = (r) => (TONES[rowTone(r)] || TONES.clear).rank;
    // Ascending means least severe first, so rank ascends with the click
    // direction and σ rides along with it — a "worst first" click has to put the
    // farthest-out row of the worst tone at the top, not the nearest.
    return rank(a) - rank(b) || (a.dist || 0) - (b.dist || 0);
  },
};

// Rows that make no claim on a given column. They are not a best or a worst
// value, they are unjudged — so they are pinned to the bottom in BOTH directions
// rather than filed among the healthy rows at one end and hidden at the other.
//
//   status  a cohort row with no peer to compare against (no peer cohort, an idle
//           peer, a row that IS the whole cohort — see NO_PEER_LABEL). Never true
//           of a top-level row, which always carries a quadrant class, so this
//           costs the worklist nothing.
//   name    the "Grade unknown" bucket, which is a roster-hygiene finding rather
//           than a point on the grade scale. Matches how the server orders the
//           same rows (see _split_grade).
const PINNED_LAST = {
  status: (r) => !r.status,
  name: isUnplacedGrade,
};

// One comparator factory for both tables. The direction flip and the pin have to
// be applied in this order — a pin inside the flip becomes a pin to the TOP on a
// descending click, which is exactly the bug the pin exists to avoid — and doing
// that in one place is what keeps the worklist and the cohort panels agreeing.
const compareWith = (sort) => {
  const cmp = SORTERS[sort && sort.key];
  if (!cmp) return null;
  const pinned = PINNED_LAST[sort.key];
  return (a, b) => {
    if (pinned) {
      const pa = pinned(a);
      const pb = pinned(b);
      if (pa !== pb) return pa ? 1 : -1;
    }
    return sort.dir === "asc" ? cmp(a, b) : cmp(b, a);
  };
};

// Roster-size filter steps. A fixed ladder rather than quantiles of the rows on
// screen: a threshold that moves when the breakdown changes is not a filter the
// reader can hold in their head, and "50+" means the same thing on every district.
// Steps at or above the largest row are dropped, so the control never offers a
// choice that empties the table.
const ROSTER_STEPS = [25, 50, 100, 250, 500, 1000];

// Sentinel for "don't filter on status". A real label can't collide with it, and
// a <select> value has to be a string.
const ALL_STATUSES = "__all";

// The robot filter. Three states, not a checkbox, because "has none" is a query
// somebody actually runs: how a district rosters decides how many teachers show
// up in a teacher breakdown at all, and in some districts most of the ones that
// do never had a robot (prod SY26-27: 267 of Blue Springs' 450 teacher segments).
// Those rows are neither noise to be hidden nor performance to be judged — they
// are their own finding, so the control can isolate them as easily as exclude
// them.
//
// Deliberately NOT defaulted to "has a robot". A roster with no hardware against
// it is a real thing to have found, and a filter that hides it by default would
// quietly answer "how is adoption going" with "fine, among the people who could
// adopt".
const ROBOTS_ANY = "__any";
const ROBOTS_SOME = "__some";
const ROBOTS_NONE = "__none";
const ROBOT_OPTIONS = [
  [ROBOTS_ANY, "Any"],
  [ROBOTS_SOME, "Has a robot"],
  [ROBOTS_NONE, "No robot"],
];

// All three filters, applied. Kept out of the component because it is pure list
// arithmetic and the component is already long.
//
// A null `cap` filters as ZERO here, not as "exempt". The only row that reaches
// this with a null is "No teacher on record" — the robot lookup returns null for
// it so the Under-resourced rule can't blame hardware for what is really a
// rostering gap (see the lookup in DistrictDashboard) — but for the purpose of
// "show me who has a robot" it plainly does not have one, and letting it through
// both sides of the filter put it in a list of rows that have hardware. The
// control is only ever offered on a breakdown whose rows have the concept at all,
// so nothing conceptually robot-less is being called robot-less.
const filterRows = (rows, { rosterMin, status, robots = ROBOTS_ANY }) =>
  rows.filter((r) => {
    const owned = r.cap ? r.cap.total : 0;
    return (
      r.roster >= rosterMin &&
      (status === ALL_STATUSES || statusLabel(r) === status) &&
      (robots === ROBOTS_ANY ||
        (robots === ROBOTS_SOME ? owned > 0 : owned === 0))
    );
  });

// ---- trend-cell key ---------------------------------------------------------
// What the two marks in the Trend column are. The full explanation lives in the
// plot's "How to read this" tray ("Trend is momentum ... over lesson volume"),
// which is exactly where a reader looking at an unlabelled mark will not find it:
// the cell holds a line with a dot and, under it, a strip of unevenly dark blocks,
// with nothing on screen to say the strip is lessons.
//
// So it gets a key, drawn from the same two primitives the cells use rather than
// from lookalike glyphs — if the mark ever changes, the key changes with it. Sits
// on the status legend's row, right-aligned, because both are keys to the same
// table and two legend rows above one table is a paragraph.
const TrendKey = () => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 12,
      fontSize: 11.5,
      color: MUTED,
    }}
  >
    <span>Trend</span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <Sparkline values={[0.2, 0.5, 0.35, 0.8]} width={26} height={11} color={INK} />
      <span style={{ color: INK, fontWeight: 600 }}>standards / learner</span>
    </span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <PatternBar values={[1, 3, 2, 5]} width={26} height={8} color={INK} />
      <span style={{ color: INK, fontWeight: 600 }}>lessons</span>
    </span>
  </span>
);

// Lazy per-row expansion, shared by the worklist and by every nested cohort
// panel: which rows are open, and what each one's `load` returned. `load` may
// resolve to a plain row array or to { rows, note } — `note` is a footnote the
// panel prints under its table (e.g. what a roster floor filtered out).
const useDrill = (drill, resetKey) => {
  const [openRows, setOpenRows] = useState(() => new Set());
  const [subRows, setSubRows] = useState({});
  useEffect(() => {
    setOpenRows(new Set());
    setSubRows({});
  }, [resetKey]);
  const toggle = (row, ...args) => {
    const opening = !openRows.has(row.id);
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (opening) next.add(row.id);
      else next.delete(row.id);
      return next;
    });
    // load on first open, and retry a previous failure on re-open
    const cur = subRows[row.id];
    if (!opening || (cur && !cur.error)) return;
    setSubRows((s) => ({ ...s, [row.id]: { loading: true } }));
    Promise.resolve(drill.load(row, ...args)).then(
      (res) =>
        setSubRows((s) => ({
          ...s,
          [row.id]: Array.isArray(res)
            ? { rows: res }
            : { rows: res.rows, note: res.note },
        })),
      (err) => setSubRows((s) => ({ ...s, [row.id]: { error: String(err) } }))
    );
  };
  return { openRows, subRows, toggle };
};

// Not every row has something coherent underneath it — "Grade unknown" is a
// roster-hygiene bucket, not a cohort. Such rows keep the caret *column* (so
// the table stays aligned) but get no affordance.
const expandable = (drill, row) => !drill.canExpand || drill.canExpand(row);

// One row's status cell.
//
// The kind is shown for TYPICAL rows too, not just outliers. Printing
// "Typical · 0.8σ" on the non-outlier branch would spend a word restating the
// number beside it — the sigma value is already the ranking. A kind says
// something the sigma can't ("Shallow", "Building"), so it is worth the same ink;
// typical rows just wear it in the muted treatment their name and sparkline
// already use, so the outliers still read first.
//
// Falls back to the quadrant pill when no rule claimed the row (`kind` null),
// which is also what happens with no `include=volume,dosage` on the request — a
// missing join degrades to today's behaviour rather than inventing a label.
const RowStatus = ({ row, typical = false }) => {
  if (!row.kind)
    return (
      <StatusPill
        status={row.status}
        title="Placed by reach and mastery against the district median."
      />
    );
  const kind = KINDS[row.kind];
  if (typical)
    return (
      <span style={{ color: MUTED, whiteSpace: "nowrap" }} title={kind.help}>
        {kind.label}
      </span>
    );
  return (
    <StatusPill tone={kind.tone} label={kind.label} title={kind.help} />
  );
};

// Hover text for the pattern bar. States the two numbers the Bursty rule reads,
// so a reader can see WHY a row was called bursty (or wasn't) instead of taking
// the label on trust.
const patternTitle = (row, periodWord) => {
  const total = row.volume ? row.volume.reduce((a, b) => a + b, 0) : 0;
  const parts = [`${total.toLocaleString()} lessons this school year`];
  if (row.concentration != null)
    parts.push(
      `${row.concentration.toFixed(1)}× as concentrated as even use ` +
        `(densest quarter of the year)`
    );
  if (row.idleBuckets) parts.push(`no lessons in the last ${row.idleBuckets} ${periodWord}s`);
  return parts.join(" · ");
};

// ---- Tab 1: Where to focus -------------------------------------------------
//
// `drill` (optional) makes the worklist rows expandable. It is supplied only
// where a meaningful sub-segmentation exists — today the school dimension
// (school → grade cohort → classroom) and the grade dimension (grade cohort →
// school → classroom):
//   {
//     key, load(row) -> Promise<rows | {rows, note}>,
//     colLabel, caption, emptyText,
//     compareLabel, refName(row, parent), noPeerHelp(row, parent),
//     canExpand(row),   // optional; no caret at all when it returns false
//     child: { ...the same shape, load(row, parent) }
//   }
// `key` identifies the data window; changing it clears every open row and
// cached response. `load` is expected to memoize on the caller's side, so a
// collapse/re-expand doesn't refetch. Rows come from `buildCohortRows`.
const FocusTab = ({
  quad,
  worklist,
  medReach,
  medDepth,
  maxDepth,
  interval,
  noun,
  drill,
  // Detected focus cycles for the institution in view, or null. ANNOTATION only:
  // the window is not narrowed to them (see the note on OutliersLens), so this
  // changes no number on the page — it just says which stretch of a row's own line
  // was a push.
  //
  // Bucket indices are resolved PER ROW rather than once here, because buildSeries
  // trims each segment's leading empty boundaries: bucket 3 is a different month
  // for a school that started in August than for one that started in October.
  cycles,
  // `(row) -> {working, total} | null` for ANY row on this lens, at any drill
  // depth — keyed on the segment id's own prefix, so one function serves the
  // worklist and every cohort panel under it. Undefined when robot utilization
  // hasn't loaded, which drops the column and the filter rather than showing
  // zeros nobody can tell from real ones.
  robotsFor,
}) => {
  const yMax = Math.max(maxDepth * 1.15, 1);
  const periodWord = interval || "period";
  const [expanded, setExpanded] = useState(false);

  // Layout of the pair. `pairRef` goes on the wrapper the card sits in, so the
  // measured width is the width the pair actually has to spend — see the geometry
  // constants above for why that is the number to branch on rather than the
  // viewport's.
  const [pairRef, pairWidth] = useElementWidth();
  // Sticky across the threshold — see PAIR_HYSTERESIS. Held in a ref rather than
  // state because it is derived: re-deriving it from the same pairWidth gives the
  // same answer, so a double render can't ratchet it.
  const sbsRef = useRef(false);
  if (pairWidth != null)
    sbsRef.current =
      pairWidth >= PAIR_MIN_WIDTH - (sbsRef.current ? PAIR_HYSTERESIS : 0);
  const sideBySide = pairWidth != null && sbsRef.current;
  // Canvas size in both arrangements; the height always follows from the width
  // through PLOT_ASPECT. Before the first measurement the stacked layout renders
  // at its cap — that is the size it will keep in this app's content column, so
  // the canvas does not resize itself one frame after mount.
  const avail = pairWidth == null ? PLOT_STACKED_MAX_W + CARD_PAD * 2 : pairWidth;
  const canvasW = Math.round(
    sideBySide
      ? clamp(avail * PLOT_SHARE, PLOT_COL_MIN_W, PLOT_COL_MAX_W) - CARD_PAD * 2
      : clamp(avail - CARD_PAD * 2, PLOT_MIN_W, PLOT_STACKED_MAX_W)
  );
  const canvasH = Math.round(canvasW / PLOT_ASPECT);

  // Table state. Three independent controls over one list:
  //   sort   null = the σ-ranking the list arrived in (see SORTERS)
  //   filters roster floor + one status label
  //   hover  which row the pointer is on, which the PLOT reads (below)
  const [sort, setSort] = useState(null);
  const [rosterMin, setRosterMin] = useState(0);
  const [statusPick, setStatusPick] = useState(ALL_STATUSES);
  const [robotPick, setRobotPick] = useState(ROBOTS_ANY);
  const [hoverId, setHoverId] = useState(null);
  // Which side the pointer is on. The highlight is identical either way except
  // for the name label the plugin draws beside the raised dot: that label exists
  // for the table -> plot direction, where the pointer is 400px away in a table
  // row and Chart.js draws no tooltip. Hovering the dot itself already gets the
  // tooltip, and drawing both stacks them on top of each other — the same school
  // named twice, one name under the other's box.
  const [hoverSrc, setHoverSrc] = useState(null);
  const hoverFrom = (src) => (id) => {
    setHoverId(id);
    setHoverSrc(id == null ? null : src);
  };

  // Filter, then sort. Sorting a filtered list and filtering a sorted one give
  // the same answer, but doing it in this order means the comparator runs over
  // fewer rows and, more importantly, that "showing 4 of 11" counts what the
  // reader can see rather than what survived one of the two steps.
  // Robot counts resolved once per row, here rather than in the cells, because
  // the column, the sort comparator and the filter all read the same number and
  // three lookups of it is three chances to disagree.
  const capRows = useMemo(
    () => worklist.map((r) => ({ ...r, cap: robotsFor ? robotsFor(r) : null })),
    [worklist, robotsFor]
  );
  // The column appears only where the rows have the concept at all — a grade or
  // engagement breakdown gets null everywhere and shows no column rather than a
  // column of dashes.
  const showRobots = capRows.some((r) => r.cap);
  const cols = WORKLIST_COLS + (showRobots ? 1 : 0);

  const rows = useMemo(() => {
    const kept = filterRows(capRows, {
      rosterMin,
      status: statusPick,
      robots: robotPick,
    });
    const cmp = sort ? compareWith(sort) : null;
    // stable within ties: the incoming order is the σ-ranking, so two rows with
    // the same roster still come out worst-first
    return cmp ? [...kept].sort(cmp) : kept;
  }, [capRows, rosterMin, statusPick, robotPick, sort]);
  const filtered = rows.length !== capRows.length;
  const shown = expanded ? rows : rows.slice(0, DEFAULT_ROWS);

  // The two filters sit above the PLOT and govern both panels, so the scatter has
  // to say which dots the table is still talking about. Ids rather than row
  // objects: `rows` is a filtered + sorted copy of `worklist` while the plot draws
  // `quad`, so the same institution is two different objects in the two arrays.
  const keptIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const inFilter = (s) => keptIds.has(s.id);
  // Excluded dots are drawn HOLLOW, never removed, and the crosshair does NOT
  // move.
  //
  // Both of those are the same decision. The medians, the robust scale and every
  // row's sigma/status come from buildQuadrant over the WHOLE set, so "normal"
  // here means normal for the district — a reference frame, not a property of the
  // current selection. Recomputing it per filter would make the Status filter
  // redefine the statuses it filters on (keep only "Reaching few", the medians
  // move, some of those rows are no longer reaching few) and would quietly
  // recolour every pill in the table. And once the crosshair is fixed, the cloud
  // has to stay: a median line through a cloud with half its points deleted sits
  // nowhere in particular, and the plot's own claim — the shape of the spread — is
  // exactly what dropping points changes.

  // Filter option lists, both derived from the rows actually present.
  const maxRoster = capRows.reduce((m, r) => Math.max(m, r.roster), 0);
  const rosterOptions = [
    [0, "Any size"],
    ...ROSTER_STEPS.filter((step) => step < maxRoster).map((step) => [
      step,
      `${step}+ learners`,
    ]),
  ];
  // Ordered by the cascade, not alphabetically, so the list reads worst-first the
  // same way the legend beside it does.
  const statusOptions = useMemo(() => {
    const present = new Set(worklist.map(statusLabel).filter(Boolean));
    const ordered = [
      ...CASCADE_ORDER.map((k) => KINDS[k].label),
      ...["critical", "warning", "good"].map((q) => STATUS[q].label),
    ].filter((label) => present.has(label));
    return [
      [ALL_STATUSES, "Any status"],
      ...[...new Set(ordered)].map((label) => [label, label]),
    ];
  }, [worklist]);

  // Plot -> table needs to be able to bring a row into view, and the table shows
  // six rows of a list that can run to forty. `revealRow` opens the list and
  // scrolls; the effect does the scrolling because the row it wants may not be
  // mounted until after `expanded` has been committed.
  //
  // The tr is found by data attribute rather than by ref: ui.js's `Row` is a plain
  // function component, so a `ref` prop would be dropped on the floor, and a
  // forwardRef there is a change to a primitive four other tabs render.
  const tableRef = useRef(null);
  const [reveal, setReveal] = useState(null);
  useEffect(() => {
    if (!reveal || !tableRef.current) return;
    const el = Array.from(
      tableRef.current.querySelectorAll("[data-row-id]")
    ).find((n) => n.dataset.rowId === String(reveal));
    if (el && el.scrollIntoView)
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    setReveal(null);
  }, [reveal, expanded]);
  // Clicking a dot is the way in to its row. Click and not hover: scrolling the
  // page under a pointer that is only passing through is the kind of help nobody
  // asked for, while a click is a request. A dot the filters excluded has no row
  // to reveal, so it does nothing rather than silently clearing the filters.
  const revealRow = (id) => {
    if (!keptIds.has(id)) return;
    setExpanded(true);
    setReveal(id);
  };

  // per-row drill-down: which rows are open, and what each one's load returned
  const { openRows, subRows, toggle } = useDrill(drill, drill && drill.key);
  // With too few schools, "outlier vs the district" is meaningless -- fall back
  // to coloring every dot by its own quadrant standing (solid, not ghost-gray).
  const outlierMode = quad.length >= MIN_OUTLIER_N;
  const outlierCount = worklist.filter((p) => p.isOutlier).length;
  // a row/dot gets full status color when we're not in outlier mode, or when it
  // is itself an outlier; otherwise it's de-emphasized context.
  //
  // Hovering the row in the table promotes it too. That is the point of the
  // hover link: a typical school is a gray dot in a gray cloud, and the reader
  // pointing at its row is asking exactly "which one is it".
  // A row the filters excluded is context by definition, whatever its sigma.
  const emph = (s) =>
    inFilter(s) && (!outlierMode || s.isOutlier || s.id === hoverId);
  const hovered = hoverId ? quad.find((p) => p.id === hoverId) : null;

  // Everything the canvas plugin draws from, refreshed on every render.
  //
  // This indirection is not optional. react-chartjs-2 (4.3.1) hands `plugins` to
  // the Chart constructor and never touches them again — its effects re-send
  // `data` and `options` and call chart.update(), so a plugin's draw hooks DO
  // re-run, but they are always the hooks captured at MOUNT. Anything a plugin
  // closed over is frozen at first paint.
  //
  // Close over `hovered` and the table→plot hover silently does nothing: the
  // plugin keeps drawing with `hovered === null` forever. Close over `quad`, the
  // medians or `outlierMode` and switching the breakdown dimension or the school
  // year repaints the dots from fresh `data` while leaving the previous
  // dimension's comet trails and crosshair underneath them. One ref read at draw
  // time avoids both, and makes the class of bug impossible to reintroduce: the
  // plugin closes over nothing but the ref.
  //
  // Assigned during render rather than from an effect, because react-chartjs-2's
  // chart.update() runs in the CHILD's passive effect, which fires before this
  // component's — a parent effect would land one paint late.
  const drawRef = useRef(null);
  drawRef.current = { quad, medReach, medDepth, outlierMode, hovered, inFilter, hoverSrc };

  // Crosshair + comet trails are drawn under the points (beforeDatasetsDraw);
  // the dots themselves are real Chart.js scatter points (below) so hover
  // hit-testing + tooltips work. Captions go on top (afterDatasetsDraw).
  //
  // Built once (empty deps) to make the point above self-evident: a plugin that is
  // never replaced should not look like one that is.
  //
  // Practical consequence when editing this: HOT RELOAD DOES NOT SHOW PLUGIN
  // CHANGES. Fast Refresh keeps the memoized object, and react-chartjs-2 would
  // ignore a new one anyway, so the running chart keeps drawing with the code that
  // was live at mount. Reload the page. (Edits to `data` or `options` below do hot
  // reload normally, which makes the asymmetry easy to trip over.)
  const cometPlugin = useMemo(() => ({
    id: "comet",
    beforeDatasetsDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      if (!chartArea) return;
      const { quad, medReach, medDepth, outlierMode, hovered, inFilter } =
        drawRef.current;
      const emph = (s) =>
        inFilter(s) && (!outlierMode || s.isOutlier || s === hovered);
      const X = (v) => scales.x.getPixelForValue(v);
      const Y = (v) => scales.y.getPixelForValue(v);

      ctx.save();
      // median crosshair (the district centroid / "normal")
      ctx.strokeStyle = "#c3c2b7";
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(X(medReach), chartArea.top);
      ctx.lineTo(X(medReach), chartArea.bottom);
      ctx.moveTo(chartArea.left, Y(medDepth));
      ctx.lineTo(chartArea.right, Y(medDepth));
      ctx.stroke();
      ctx.setLineDash([]);

      // comet trails: colored + prominent for emphasized schools, faint gray
      // for de-emphasized context (only in outlier mode).
      //
      // The hovered row is drawn LAST so its trail sits over the cloud rather
      // than under whichever schools happen to come later in the list — the whole
      // value of the hover link is being able to find one path among many.
      const order = hovered
        ? [...quad.filter((s) => s !== hovered), hovered]
        : quad;
      order.forEach((s) => {
        const isHover = s === hovered;
        // An excluded row keeps its dot and loses its trail. Trails are the
        // busiest ink on the plot and one faint enough to match a hollow dot
        // would not be readable anyway, so what is left tracing a path is what
        // the table has rows for. A hovered dot still draws its own, even when excluded — the
        // pointer is a question and answering it beats consistency.
        if (!inFilter(s) && !isHover) return;
        const col = emph(s) ? markColor(s) : CONTEXT;
        const tail = s.tail;
        if (tail.length > 1) {
          for (let i = 1; i < tail.length; i++) {
            const a = tail[i - 1];
            const b = tail[i];
            ctx.strokeStyle = col;
            const base = isHover ? 0.35 : emph(s) ? 0.12 : 0.05;
            const ramp = isHover ? 0.65 : emph(s) ? 0.5 : 0.18;
            ctx.globalAlpha = base + ramp * (i / (tail.length - 1));
            ctx.lineWidth = isHover ? 3 : 2;
            ctx.beginPath();
            ctx.moveTo(X(a.reach), Y(a.depth));
            ctx.lineTo(X(b.reach), Y(b.depth));
            ctx.stroke();
          }
        }
      });
      ctx.globalAlpha = 1;

      // The hovered dot, RAISED — the same device the concern nav's buttons use:
      // a soft drop shadow with a downward offset, so the mark reads as lifted off
      // the plot rather than as merely bigger.
      //
      // A shadowed disc drawn here, immediately under the real point, rather than
      // any change to the point itself. Two reasons it has to work this way:
      // Chart.js has no per-point shadow, and — more importantly — the mark must
      // not MOVE. A CSS button can lift 1px because its position means nothing; a
      // scatter point's position is the datum, and shifting it up two pixels is a
      // lie about the school's mastery. So the shadow does all the lifting and the
      // dot stays exactly on its coordinates.
      //
      // Drawn as a WHITE disc a couple of pixels proud of the dot, so it supplies
      // both halves of the effect: the shadow beneath, and a rim wider than the
      // point's own 1.5px white border. Chart.js then paints the real dot on top,
      // centred, and the result reads as a chip lifted off the plot — without a
      // single Chart.js property having changed (see pointBorderWidth below).
      if (hovered) {
        const cx = X(hovered.reach);
        const cy = Y(hovered.depth);
        ctx.shadowColor = "rgba(43, 42, 40, 0.42)";
        ctx.shadowBlur = 10;
        ctx.shadowOffsetY = 3;
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(cx, cy, dotRadius(hovered) + 2.5, 0, 2 * Math.PI);
        ctx.fill();
      }
      ctx.restore();
    },
    afterDatasetsDraw(chart) {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;
      const { hovered, hoverSrc } = drawRef.current;
      ctx.save();

      // Repaint the hovered dot in its own tone. In outlier mode a typical row's
      // dot is context gray at 33% alpha, and the raised white chip under it left
      // the mark paler than its neighbours — lifted but washed out. The table
      // already promotes a hovered row this way (its name goes to ink, its trend
      // line to its tone), so the dot has to agree or hovering makes the row and
      // its dot disagree about how emphasized they are.
      //
      // Done HERE, over the top of the point Chart.js drew, rather than by making
      // `pointBackgroundColor` hover-dependent. Colour alone would in fact be
      // layout-safe — only radius and border feed getMaxOverflow — but "no part of
      // the chart config varies with the hover" is a rule worth keeping whole
      // rather than one with a footnote about which properties are safe.
      if (hovered) {
        const { scales } = chart;
        const hx = scales.x.getPixelForValue(hovered.reach);
        const hy = scales.y.getPixelForValue(hovered.depth);
        ctx.beginPath();
        ctx.arc(hx, hy, dotRadius(hovered), 0, 2 * Math.PI);
        ctx.fillStyle = markColor(hovered);
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "#fff";
        ctx.stroke();
      }

      ctx.font = "600 10px system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.textBaseline = "top";
      // Region captions, toned like the dots that land in them: a school in the
      // low-mastery corner draws an amber dot, so the corner is amber. They read
      // the QUADRANT tone rather than a row's, since a region has no kind — which
      // is the one legitimate use of the quadrant palette left.
      //
      // `ink`, not `color`: these are WORDS on white, and the amber that reads
      // fine as a 20px dot is 2.2:1 as 10px type — the exact split TONES.ink
      // exists for. The dots beside them keep `color`, so the pairing still reads
      // as one hue per corner.
      ctx.fillStyle = TONES[QUADRANT_TONE.good].ink;
      ctx.textAlign = "right";
      ctx.fillText("THRIVING", chartArea.right - 6, chartArea.top + 4);
      ctx.fillStyle = TONES[QUADRANT_TONE.warning].ink;
      ctx.textAlign = "left";
      ctx.fillText("REACHING FEW", chartArea.left + 6, chartArea.top + 4);
      ctx.fillStyle = TONES[QUADRANT_TONE.critical].ink;
      ctx.fillText("LOW MASTERY", chartArea.left + 6, chartArea.bottom - 16);

      // The hovered row, NAMED on the plot. The shadow says "this one is lifted";
      // the label says which one, which is the part a reader 400px down the table
      // actually needs — without it they still hover, find a raised dot, and then
      // check the tooltip to confirm it is the row they pointed at.
      //
      // No ring any more: the raised disc is the highlight, and a ring on top of a
      // shadow is two highlights arguing about which one is the highlight.
      //
      // Flips to the left of the dot near the right edge so a long school name
      // doesn't run off the canvas.
      // Skipped when the pointer is on the canvas: Chart.js's tooltip is already
      // naming this dot, and it draws over exactly this spot.
      if (hovered && hoverSrc !== "plot") {
        const { scales } = chart;
        const cx = scales.x.getPixelForValue(hovered.reach);
        const cy = scales.y.getPixelForValue(hovered.depth);
        const gap = dotRadius(hovered) + 8;
        ctx.font = "700 11.5px system-ui, -apple-system, 'Segoe UI', sans-serif";
        ctx.textBaseline = "middle";
        const flip =
          cx + gap + ctx.measureText(hovered.name).width > chartArea.right;
        ctx.textAlign = flip ? "right" : "left";
        const tx = flip ? cx - gap : cx + gap;
        // a halo, so the name stays readable where it crosses a gridline
        ctx.lineWidth = 3.5;
        ctx.strokeStyle = "#fff";
        ctx.strokeText(hovered.name, tx, cy);
        ctx.fillStyle = INK;
        ctx.fillText(hovered.name, tx, cy);
      }
      ctx.restore();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // real scatter points (one per school) carry their meta for the tooltip.
  //
  // No size bump on hover: the raised disc is the highlight, and growing the dot
  // as well would make the mark's own encoding (radius = roster) lie for as long
  // as the pointer sits on the row.
  const radii = quad.map(dotRadius);
  const data = {
    datasets: [
      {
        label: noun.many,
        data: quad.map((s) => ({
          x: s.reach,
          y: s.depth,
          name: s.name,
          roster: s.roster,
          reach: s.reach,
          depth: s.depth,
          status: s.status,
          isOutlier: s.isOutlier,
          dist: s.dist,
        })),
        pointRadius: radii,
        pointHoverRadius: radii.map((r) => r + 2),
        pointBackgroundColor: quad.map((s) =>
          dotColor(s, outlierMode, !inFilter(s))
        ),
        // White where the dot is filled — that border is what makes a mark read
        // as a disc lying on top of the cloud. For an excluded dot the border is
        // the only ink there is (see dotColor), so it carries the outline
        // instead. Colour only: the WIDTH stays constant for the reason below.
        pointBorderColor: quad.map((s) =>
          inFilter(s) ? "#fff" : CONTEXT
        ),
        // Constant, and it MUST stay constant. Chart.js sizes its own auto-padding
        // from `getMaxOverflow()`, which for a scatter is
        //   Math.max(border, firstPoint.size, lastPoint.size) / 2
        // where size is radius + borderWidth. It samples the FIRST AND LAST points
        // only — so a per-point border bump on hover moves the chart area, but
        // only when the hovered row happens to sit at one end of the dataset.
        // Viewing Dillon by grade, hovering Grade 5 visibly compresses the y-axis
        // and hovering Grade 2 does not, which is not even a consistent bug.
        //
        // Hence the rule this file follows: NOTHING in `data` or `options` is
        // allowed to vary with the hover. The whole highlight is drawn by the
        // plugin, on the canvas, where it cannot participate in layout.
        pointBorderWidth: 1.5,
        // Chart.js keeps its own hover state for pointer-over-canvas; this is the
        // TABLE's hover, so it has to ride on the base style rather than the hover
        // style or the two would fight over the same dot.
        pointHitRadius: 6,
      },
    ],
  };
  // Frame only, no categoryScales: both axes here are continuous measures with
  // their own bounds and a visible grid, which is the opposite of the binned-x
  // convention the other four charts take.
  const options = chartOptions({
    interaction: { mode: "nearest", intersect: true },
    // Off because the table's hover re-renders this chart: with animation on, a
    // pointer moving down the rows leaves every dot easing toward its new radius
    // and the plot ripples instead of answering the question.
    animation: false,
    // Plot -> table. The table -> plot direction has been here from the start;
    // this is its mirror, and it costs one callback because both directions are
    // the same single piece of state. Chart.js's own hover state is left to drive
    // the tooltip; everything else on the page reads `hoverId`.
    //
    // `active` is empty on mouseout, so this clears itself. The wrapper's
    // onMouseLeave is a belt-and-braces clear for the case where the pointer
    // leaves the canvas fast enough to skip the final mousemove.
    onHover: (_e, active) => {
      const hit = active && active.length ? quad[active[0].index] : null;
      const id = hit ? hit.id : null;
      if (id !== hoverId) hoverFrom("plot")(id);
    },
    onClick: (_e, active) => {
      if (active && active.length) revealRow(quad[active[0].index].id);
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (items) => (items.length ? items[0].raw.name : ""),
          label: (ctx) => {
            const r = ctx.raw;
            return [
              `Reached: ${r.reach.toFixed(0)}%`,
              `Std / learner: ${r.depth.toFixed(1)}`,
              `Roster: ${r.roster}`,
              r.isOutlier
                ? `${STATUS[r.status].label} · ${r.dist.toFixed(1)}σ from normal`
                : `Typical · ${r.dist.toFixed(1)}σ`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        title: axisTitle("% of roster reached →"),
        min: 0,
        max: 100,
        grid: { color: GRID },
      },
      y: {
        title: axisTitle("standards mastered / active learner →"),
        min: 0,
        max: yMax,
        grid: { color: GRID },
      },
    },
  });

  // What the population is, said once for both panels. The ORDER of the rows is a
  // separate sentence and lives on the table (below) — it changes when you click a
  // column header, and a line 300px above the table it describes is a line nobody
  // sees change.
  const populationLine = (
    <>
      {!outlierMode
        ? `too few ${noun.many} to rank by outlier`
        : outlierCount
        ? `${outlierCount} outlier${outlierCount > 1 ? "s" : ""}`
        : `no ${noun.many} past the outlier threshold`}
      {filtered &&
        ` · ${rows.length} of ${worklist.length} shown, the rest hollow on the plot`}
    </>
  );
  const orderLine = sort ? (
    <>
      {sortPhrase(sort, noun, rows)}
      {" ("}
      {/* the σ-ranking is not reachable from any column header, so a sorted table
          needs an explicit way back to it */}
      <button
        type="button"
        onClick={() => setSort(null)}
        style={{
          border: "none",
          background: "none",
          padding: 0,
          font: "inherit",
          color: "inherit",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        back to priority order
      </button>
      {")"}
    </>
  ) : outlierMode ? (
    "ranked by distance from normal"
  ) : (
    `showing each ${noun.one}'s standing`
  );

  return (
    <>
      {/* One head for the pair, above both views of it — which is a reversal of
          what this file used to say ("the only heading on the lens belongs to the
          table"). That held while the two were independent: a heading over the plot
          made the plot the subject and the table its footnote.

          They are not independent any more; they are one card (see below). The
          Roster and Status filters govern both, so they cannot live on the table's
          heading — a control that dims half the scatter cannot be introduced below
          the scatter — and once the filters move up, the head they sit on is the
          head for the pair. Nothing is left between the two views but the card's
          own divider.

          "Compare" rather than "Needs attention": with a roster filter, a status
          filter and five sortable columns this is where you go to compare
          {noun.many} on any of these measures, not a fixed exception report. */}
      <SectionHead
        title={`Compare ${noun.many}`}
        sub={populationLine}
        controls={
          <>
            <Select
              inline
              label="Roster"
              value={rosterMin}
              onChange={(v) => setRosterMin(parseInt(v, 10))}
              title={`Drop ${noun.many} below this roster size from the table — small rosters make reach and depth jumpy. Their dots stay on the plot, drawn hollow, so the spread and the median don't change.`}
              options={rosterOptions}
            />
            {showRobots && (
              <Select
                inline
                label="Robots"
                value={robotPick}
                onChange={setRobotPick}
                title={`Whether a ${noun.one} has any robot assigned to it. Rows this drops go hollow on the plot rather than leaving it, so the spread and the median keep describing the same district.`}
                options={ROBOT_OPTIONS}
              />
            )}
            <Select
              inline
              label="Status"
              value={statusPick}
              onChange={setStatusPick}
              maxWidth={190}
              title="Keep only rows with this status. Everything else goes hollow on the plot rather than leaving it."
              options={statusOptions}
            />
          </>
        }
      />
      {/* Plot and table are ONE card, and which way round depends on the width
          this pair got — see the geometry constants at the top of the file.

          They are one card because they are one object: the same rows, the same
          filters over both, a hover on either lighting up the other, a click on a
          dot revealing its row. As two cards with a caption, a sort line and two
          keys stacked between them, the pair spent ~90px on the seam and read as a
          result followed by a footnote. The plot is the complement here — it
          summarises visually what the table states precisely — so it gets the
          smaller share and the table keeps the names, the numbers and the
          drill-downs.

          Everything explanatory now sits BELOW the pair (the PlotNote at the
          bottom), except the two keys, which each sit inside the view they key:
          the status colours under the plot whose dots wear them, the trend marks
          on the table band whose column they explain. */}
      <div ref={pairRef}>
        <Card
          pad={0}
          style={{ overflow: "hidden", display: sideBySide ? "flex" : "block" }}
        >
          {/* onMouseLeave clears the hover for the pointer that leaves the canvas
              without a last mousemove over empty plot area (a fast exit past the
              edge). The cursor turns into a pointer over a dot because a dot is
              clickable — it reveals its row in the table. */}
          <div
            onMouseLeave={() => hoverFrom("plot")(null)}
            style={{
              padding: CARD_PAD,
              cursor: hoverId ? "pointer" : "default",
              display: "flex",
              // Stacked, the key goes in the gutter BESIDE the plot; side by side
              // there is no gutter, so it goes under it. See KindLegend's
              // `column`.
              flexDirection: sideBySide ? "column" : "row",
              justifyContent: "center",
              alignItems: "center",
              gap: sideBySide ? 10 : 20,
              // Side by side the column is exactly as wide as the canvas it
              // holds and does not flex; stacked it is a centred figure in a
              // full-width card.
              ...(sideBySide
                ? { flex: `0 0 ${canvasW + CARD_PAD * 2}px` }
                : null),
            }}
          >
            <div style={{ width: canvasW, height: canvasH, flex: "0 0 auto" }}>
              <Scatter data={data} options={options} plugins={[cometPlugin]} />
            </div>
            {/* The status key belongs to the DOTS as much as to the pills — a row
                and its dot are one colour by construction (see markColor) — and
                until it moved here the plot had no key to its own colours at all.
                Inside the plot's padding, so the seam between the two views stays
                a single divider. */}
            <div style={sideBySide ? { width: canvasW } : { minWidth: 0 }}>
              <KindLegend rows={rows} column={!sideBySide} />
            </div>
          </div>
          <div
            style={{
              minWidth: 0,
              // The divider is the seam. Side by side it has to span the taller
              // of the two columns, which is why the card does not set
              // alignItems: a stretched flex child carries its border the whole
              // way down.
              ...(sideBySide
                ? { flex: "1 1 auto", borderLeft: `1px solid ${CARD_BORDER}` }
                : { borderTop: `1px solid ${CARD_BORDER}` }),
            }}
          >
            {/* The two lines that are about the table and only the table: how the
                rows are ordered, and what the two Trend marks in them mean. On the
                table's own band, directly above the header row they explain.

                The order line used to be half of a heading's sub-line, sharing it
                with the outlier count. They were never the same sentence: the
                count is a fact about the district (so it stays up on the head with
                the filters that produce it), and the order is a fact about this
                list. */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                flexWrap: "wrap",
                gap: "4px 16px",
                padding: `10px ${CARD_PAD}px`,
              }}
            >
              <div
                style={{
                  color: MUTED,
                  fontSize: 12,
                  flex: "1 1 auto",
                  minWidth: 0,
                }}
              >
                {orderLine}
              </div>
              <TrendKey />
            </div>
            {/* The wrapper exists only so `revealRow` has something to query for
                the row a clicked dot belongs to — see the note on `reveal` above. */}
            <div ref={tableRef}>
        <Table fixed minWidth={TABLE_MIN_WIDTH}>
          <HeadRow>
            {drill && <Th width={26} />}
            <SortTh
              sortKey="name"
              sort={sort}
              onSort={setSort}
              defaultDir={SORT_DIR.name}
            >
              {noun.One}
            </SortTh>
            {measureCols(sort, setSort, showRobots)}
            <SortTh
              sortKey="status"
              sort={sort}
              onSort={setSort}
              defaultDir={SORT_DIR.status}
              width={168}
              title={`Sort by severity${outlierMode ? ", then distance from normal" : ""}`}
            >
              Status
            </SortTh>
          </HeadRow>
          <tbody>
            {shown.map((p) => {
              const open = openRows.has(p.id);
              const bands = cycleBandsForSeries(p.series, cycles);
              const isHover = p.id === hoverId;
              return (
                <React.Fragment key={p.id}>
                  <Row
                    data-row-id={p.id}
                    onMouseEnter={() => hoverFrom("table")(p.id)}
                    onMouseLeave={() => {
                      if (hoverId === p.id) hoverFrom("table")(null);
                    }}
                    // The tint is load-bearing in BOTH directions. Table ->
                    // plot it says which row lifted the dot; plot -> table it is
                    // the entire answer — the pointer is over the canvas, so
                    // nothing else on the row can indicate itself.
                    style={isHover ? { background: nestBg(1) } : undefined}
                  >
                    {drill && (
                      <Td style={{ padding: "8px 0 8px 4px" }}>
                        {expandable(drill, p) && (
                          <Caret
                            open={open}
                            onClick={() => toggle(p)}
                            title={`${open ? "Hide" : "Show"} ${
                              drill.colLabel || "detail"
                            } for ${p.name}`}
                          />
                        )}
                      </Td>
                    )}
                    <Td
                      truncate
                      title={p.name}
                      style={{
                        fontWeight: outlierMode && p.isOutlier ? 600 : 400,
                        color: emph(p) ? INK : "#6b6a66",
                      }}
                    >
                      {p.name}
                    </Td>
                    <Td align="right">{p.roster}</Td>
                    {showRobots && (
                      <Td
                        align="right"
                        title={robotTitle(p.cap, noun.one)}
                        style={{
                          // A zero here is the point of the column, so it is not
                          // allowed to read as a faint also-ran beside the other
                          // measures.
                          color:
                            p.cap && !p.cap.total
                              ? TONES.attention.ink
                              : undefined,
                          fontWeight: p.cap && !p.cap.total ? 600 : undefined,
                        }}
                      >
                        {robotCount(p.cap) == null ? "—" : robotCount(p.cap)}
                      </Td>
                    )}
                    <Td align="right">
                      {p.reach.toFixed(0)}%
                      <DeltaChip
                        value={p.reachDelta}
                        digits={1}
                        unit="pp"
                        period={periodWord}
                      />
                    </Td>
                    <Td align="right">
                      {p.depth.toFixed(1)}
                      <DeltaChip
                        value={p.depthDelta}
                        digits={1}
                        period={periodWord}
                      />
                    </Td>
                    {/* Momentum over lesson volume. Two quantities, stacked in
                        one cell so the shared column geometry (and the vertical
                        register with every nested cohort panel) survives adding a
                        mark. The pattern bar is the district ribbon's encoding at
                        one-row scale, and both carry the same cycle band. */}
                    <Td>
                      <Sparkline
                        values={p.momentum}
                        // the row's own tone, so this line, the pill two columns
                        // right, and the row's dot on the scatter above are one
                        // colour
                        color={emph(p) ? markColor(p) : CONTEXT}
                        bands={bands}
                      />
                      {p.volume && (
                        <div style={{ marginTop: 3 }}>
                          <PatternBar
                            values={p.volume}
                            // matches the sparkline above it: two marks about the
                            // same window, so their bucket edges must line up
                            width={96}
                            color={emph(p) ? INK : CONTEXT}
                            bands={bands}
                            title={patternTitle(p, periodWord)}
                          />
                        </div>
                      )}
                    </Td>
                    <Td>
                      {!outlierMode ? (
                        <RowStatus row={p} />
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <RowStatus row={p} typical={!p.isOutlier} />
                          <span style={{ color: MUTED, fontSize: 11 }}>
                            {p.dist.toFixed(1)}σ
                          </span>
                        </span>
                      )}
                    </Td>
                  </Row>
                  {drill && open && (
                    <Row flush>
                      <Td
                        colSpan={cols + 1}
                        style={{ padding: 0, background: nestBg(1) }}
                      >
                        <CohortPanel
                          state={subRows[p.id]}
                          drill={drill}
                          parent={p}
                          periodWord={periodWord}
                          robotsFor={robotsFor}
                          showRobots={showRobots}
                        />
                      </Td>
                    </Row>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
        {/* A filter that empties the table has to say so — a bare head row with a
            legend above it reads as a failed request. Inside the card and on the
            divider, so the sortable headers stay put and stay usable. */}
        {!rows.length && (
          <TableNote>
            No {noun.many} match these filters.{" "}
            <GhostButton
              onClick={() => {
                setRosterMin(0);
                setStatusPick(ALL_STATUSES);
              }}
              style={{ marginLeft: 4 }}
            >
              Clear filters
            </GhostButton>
          </TableNote>
        )}
            </div>
          </div>
        </Card>
      </div>
      {/* Counts the FILTERED list, not the whole worklist: with a roster floor
          set, "Show all 17 schools" over a nine-row table is just wrong. */}
      {rows.length > DEFAULT_ROWS && (
        <GhostButton
          onClick={() => setExpanded((x) => !x)}
          style={{ marginTop: 12 }}
        >
          {expanded
            ? "Show fewer"
            : `Show all ${rows.length} ${
                filtered ? `matching ${noun.many}` : noun.many
              }`}
        </GhostButton>
      )}
      {/* The caption sits BELOW the pair, not between the two halves of it. The
          reading order PlotNote was built for — see the plot, then read one line
          saying what it is, then the caveats if you want them — still holds; the
          only change is that "the plot" is now the plot AND the table it is joined
          to, so the one line describing them comes after both rather than in the
          seam. */}
      <PlotNote
        style={{ margin: "16px 0 0" }}
        details={
          <>
            <strong>Reach</strong> is the share of the {noun.one}'s roster that
            has engaged this school year; <strong>depth</strong> is standards
            mastered per <em>engaged</em> learner, so it doesn't move when reach
            does. Bubble size is roster, and the crosshair is the district
            median — over every {noun.one}, not just the filtered ones, so it
            stays put as you filter. Rows the filters drop go hollow and lose
            their trail rather than leaving the plot, which is what keeps the
            spread and that median describing the same district.{" "}
            {outlierMode ? (
              <>
                Color marks the outliers — {noun.many} ≥ {OUTLIER_SIGMA}σ from
                that center — while typical {noun.many} stay gray, and stronger
                color means farther out.
              </>
            ) : (
              <>
                With too few {noun.many} to flag outliers, each dot is colored by
                its standing (thriving / reaching few / low mastery) instead.
              </>
            )}{" "}
            The comet trail is the path over the window: evenly spaced for steady
            use, a cluster that jumps once and stops for a concentrated push.
            Every number counts only events inside the selected school year. In
            the table, Trend is momentum (new standards per learner each{" "}
            {periodWord}) over lesson volume — denser means more lessons — and
            the deltas are the change over the last {periodWord}.
            {cycles && cycles.length ? (
              <>
                {" "}
                The shaded {cycles.length > 1 ? "bands mark" : "band marks"} the
                district's{" "}
                {cycles.map((c) => c.label.toLowerCase()).join(" and ")} —
                annotation only; every number still describes the whole year.
              </>
            ) : null}
          </>
        }
      >
        Each {noun.one} by <strong>reach</strong> (% of roster engaged) against{" "}
        <strong>depth</strong> (standards per engaged learner), sized by roster.
        {outlierMode
          ? ` Colored dots sit ≥ ${OUTLIER_SIGMA}σ from the district median.`
          : ` Dots are colored by standing; too few ${noun.many} to flag outliers.`}
      </PlotNote>
    </>
  );
};

// Why a cohort row got the status it did — the hover text on its pill.
//
// The top-level worklist has explained its own pills from the start (RowStatus
// passes KINDS[].help, or the median sentence for a quadrant pill) and the rows
// you drill into never did, which is the worse gap of the two: a cohort's status
// is a DIFFERENT RULE wearing the same three words. It is not placement against
// the district median at all — it is this row against the ONE peer named on the
// line underneath it, with a tolerance band around it (classifyVsPeer). A reader
// who carries the parent row's meaning down into the expansion reads "Thriving"
// as "thriving in the district" when it means "keeping up with its peer", so the
// text says which reference is in play as well as which test the row failed.
//
// Numbers stay out of it: the reference line directly below the pill already
// prints the peer's reach and depth, and the row's own are two columns left.
// The cohort level's OWN vocabulary. It shares `classifyVsPeer`'s three keys with
// the district quadrant, and for a while it shared the quadrant's three words too
// — which was the real defect behind "why is the sub-group rule different?".
//
// The rule has to be different. A school is one of many peers, so it has a
// distribution: a median, a robust scale, and a σ that makes "outlier" mean
// something. A drilled row has exactly ONE reference point, so there is no spread
// to measure and `classifyVsPeer`'s fixed tolerance band is what is left. More
// importantly the drill exists to kill a confound — a grade cohort judged against
// the median across schools would mostly report grade level, since fifth graders
// master more standards than kindergartners everywhere — so pushing the parent's
// rule down would report grade level dressed as school performance.
//
// What could NOT stay is both levels spending the same three words on two
// different claims. "Thriving" on a school meant "top-right of the district
// scatter"; "Thriving" on a grade cohort meant "within 10% of its own district
// cohort". A reader carrying the parent's meaning one level down was reading a
// sentence nobody wrote. So the words here name the comparison instead of a
// verdict, and every one of them is relative: BEHIND, KEEPING UP, AHEAD.
//
// Label and explanation live in one table because they are one statement. Tones
// still come from QUADRANT_TONE on the same key, so a row's colour is unchanged
// and its dot, sparkline and pill stay one colour (see rowTone).
const PEER_STATUS = {
  critical: {
    label: "Behind on mastery",
    help: (ref, band) =>
      `Standards per engaged learner more than ${band} below ${ref}.`,
  },
  warning: {
    label: "Behind on reach",
    help: (ref, band) =>
      `Mastery per engaged learner keeps up with ${ref}, but reach is more ` +
      `than ${band} below it.`,
  },
  good: {
    label: "Keeping up",
    help: (ref, band) =>
      `Reach and mastery both within ${band} of ${ref}, or ahead of it.`,
  },
};

const peerLabel = (row) =>
  (PEER_STATUS[row.status] && PEER_STATUS[row.status].label) || null;

// Hover text for a cohort row's pill. The reference is named in it because the
// words above are comparative and a comparison with an unnamed other side is not
// a statement — and the closing sentence says which reference, because the row
// one level up used a different one.
const cohortStatusHelp = (row, refLabel) => {
  const entry = PEER_STATUS[row.status];
  if (!entry) return undefined;
  const band = `${Math.round(COHORT_PEER_TOLERANCE * 100)}%`;
  return (
    entry.help(refLabel, band) +
    ` Judged against that peer, not against the district median.`
  );
};

// Why a cohort row shows no status. Keyed by buildCohortRows' `peerState`; the
// label goes in the cell, the explanation in its tooltip. Each drill level
// supplies its own prose via `drill.noPeerHelp` (and may override any label via
// `drill.noPeerLabel`) — the reference means something different at each one
// (district grade vs the cohort a classroom sits in).
//
// Cohort names come from the server verbatim ("PreK", "Kindergarten",
// "Grade 3") and keep their casing wherever they appear — lowercasing them for
// prose turns PreK into "prek". Sentences are phrased so a capitalized label
// sits naturally.
const NO_PEER_LABEL = {
  sole: "Only cohort",
  idle: "No peer activity",
  none: "No peer cohort",
};

// Expanded-row body: one sub-row per segment inside the row above, carrying the
// same measures as its parent (roster / reached / std per learner / trend /
// status). Status here is a *peer* comparison, not the district crosshair — see
// buildCohortRows for why a grade has to be judged against the same grade.
//
// Renders itself again for `drill.child`, so the worklist nests
// school → grade cohort → classroom without a second table implementation.
const CohortPanel = ({
  state,
  drill,
  parent,
  periodWord,
  robotsFor,
  // Inherited from the parent table rather than re-derived from these rows: the
  // two tables are the same measures at different depths and their column widths
  // have to line up (see measureCols). A grade level under a school breakdown has
  // no robot count of its own, and it still shows the column — with dashes —
  // because the level under IT is teachers, which does.
  showRobots = false,
  depth = 1,
}) => {
  const child = drill.child;
  const cols = COHORT_COLS + (showRobots ? 1 : 0);
  const { openRows, subRows, toggle } = useDrill(child, child && child.key);
  // Sort state is per PANEL, not shared with the parent table or with sibling
  // panels. Two expanded rows are two different populations — the grades at one
  // school and the grades at another — and a click on one ordering the other is a
  // change the reader did not ask for and cannot see. It also disappears with the
  // panel, which is right: the panel unmounts on collapse, so re-expanding gives
  // you the level's own order back.
  const [sort, setSort] = useState(null);
  // Decorated and ordered in one memo, so the robot count the Robots column shows
  // is the one its comparator sorted on. `null` sort keeps the order the level
  // arrived in, which is the server's (schools and teachers by name, grades
  // ascending) — a meaningful default that no column header can express.
  const rows = useMemo(() => {
    const base = (state && state.rows) || [];
    const withCap = base.map((c) => ({
      ...c,
      cap: robotsFor ? robotsFor(c) : null,
    }));
    const cmp = sort ? compareWith(sort) : null;
    return cmp ? [...withCap].sort(cmp) : withCap;
  }, [state, sort, robotsFor]);
  const leftPad = 12 + depth * 22;
  const pad = { padding: `6px 8px 6px ${leftPad}px` };
  if (!state || state.loading)
    return (
      <div style={{ ...pad, color: MUTED, fontSize: 12.5 }}>
        <FA color="gray" icon="spinner" spin /> Loading{" "}
        {drill.colLabel || "detail"}…
      </div>
    );
  if (state.error)
    return (
      <div style={{ ...pad, color: STATUS.critical.color, fontSize: 12.5 }}>
        Couldn't load {drill.colLabel || "detail"} for {parent.name}.
      </div>
    );
  if (!rows.length)
    return (
      <div style={{ ...pad, color: MUTED, fontSize: 12.5 }}>
        {state.note ||
          drill.emptyText ||
          `No ${drill.colLabel || "detail"} with an enrolled roster here.`}
      </div>
    );
  const refName = drill.refName || ((c) => `district ${c.name}`);
  const noPeerLabel = { ...NO_PEER_LABEL, ...(drill.noPeerLabel || {}) };
  const helpFor = (c) =>
    drill.noPeerHelp
      ? drill.noPeerHelp(c, parent)
      : `No ${c.name} cohort to compare against.`;
  // The word for THIS level, not the parent's. `noun.one` here would tell a
  // reader that robots are not a property of a "school" while they are hovering
  // a grade cohort row inside one.
  const levelWord = (drill.colLabel || "segment").toLowerCase();
  return (
    <div style={{ padding: `8px 8px 12px ${leftPad}px` }}>
      {/* The caption says what this level IS; the sort phrase says how it is
          ordered. One line, because the second only exists once you have clicked
          a header and the first should not move when it appears. The parent table
          spends a line of its own on this (`orderLine`); a panel nested two levels
          in cannot afford one. */}
      {(drill.caption || sort) && (
        <div
          style={{
            color: MUTED,
            fontSize: 11.5,
            margin: "0 0 6px",
            maxWidth: "82ch",
          }}
        >
          {drill.caption}
          {sort && (
            <>
              {drill.caption ? " · " : ""}
              {sortPhrase(sort, { one: levelWord }, rows, {
                status: "distance from its peer",
              })}
              {" ("}
              <button
                type="button"
                onClick={() => setSort(null)}
                style={{
                  border: "none",
                  background: "none",
                  padding: 0,
                  font: "inherit",
                  color: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                back to {levelWord} order
              </button>
              {")"}
            </>
          )}
        </div>
      )}
      <Table fixed size="sm" minWidth={TABLE_MIN_WIDTH - leftPad}>
        <HeadRow tint={false}>
          {child && <Th width={24} />}
          <SortTh
            sortKey="name"
            sort={sort}
            onSort={setSort}
            defaultDir={SORT_DIR.name}
          >
            {drill.colLabel || "Segment"}
          </SortTh>
          {measureCols(sort, setSort, showRobots)}
          <SortTh
            sortKey="status"
            sort={sort}
            onSort={setSort}
            defaultDir={SORT_DIR.status}
            width={168}
            title="Sort by how far this level is from its peer; rows with no peer to compare against stay at the bottom either way"
          >
            {drill.compareLabel || "vs. district peer"}
          </SortTh>
        </HeadRow>
        <tbody>
          {rows.map((c) => {
            const open = openRows.has(c.id);
            return (
              <React.Fragment key={c.id}>
                <Row>
                  {child && (
                    <Td style={{ padding: "6px 0 6px 2px" }}>
                      {expandable(child, c) && (
                        <Caret
                          open={open}
                          onClick={() => toggle(c, parent)}
                          title={`${open ? "Hide" : "Show"} ${
                            child.colLabel || "detail"
                          } for ${c.name}`}
                        />
                      )}
                    </Td>
                  )}
                  <Td truncate title={c.name} style={{ color: INK }}>
                    {c.name}
                  </Td>
                  <Td align="right">{c.roster}</Td>
                  {showRobots && (
                    <Td align="right" title={robotTitle(c.cap, levelWord)}>
                      {robotCount(c.cap) == null ? "—" : robotCount(c.cap)}
                    </Td>
                  )}
                  <Td align="right">
                    {c.reach.toFixed(0)}%
                    <DeltaChip
                      value={c.reachDelta}
                      digits={1}
                      unit="pp"
                      period={periodWord}
                    />
                  </Td>
                  <Td align="right">
                    {c.depth.toFixed(1)}
                    <DeltaChip
                      value={c.depthDelta}
                      digits={1}
                      period={periodWord}
                    />
                  </Td>
                  <Td>
                    <Sparkline
                      values={c.momentum}
                      width={72}
                      height={20}
                      color={c.status ? markColor(c) : CONTEXT}
                    />
                  </Td>
                  <Td>
                    {c.status ? (
                      <>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <StatusPill
                            status={c.status}
                            label={peerLabel(c)}
                            title={cohortStatusHelp(c, refName(c, parent))}
                          />
                          {c.thin && (
                            <span
                              style={{ color: MUTED, fontSize: 11 }}
                              title={
                                c.engaged === 0
                                  ? "No learner here has engaged in this window — there is nothing to read yet."
                                  : `Only ${c.engaged} learner${
                                      c.engaged === 1 ? "" : "s"
                                    } engaged — read this as an anecdote, not a trend.`
                              }
                            >
                              · thin
                            </span>
                          )}
                        </span>
                        <div
                          style={{ color: MUTED, fontSize: 11, marginTop: 2 }}
                        >
                          {refName(c, parent)}: {c.refReach.toFixed(0)}% ·{" "}
                          {c.refDepth.toFixed(1)}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: MUTED }} title={helpFor(c)}>
                        {noPeerLabel[c.peerState] || "No peer cohort"}
                      </span>
                    )}
                  </Td>
                </Row>
                {child && open && (
                  <Row flush>
                    <Td
                      colSpan={cols + 1}
                      style={{ padding: 0, background: nestBg(depth + 1) }}
                    >
                      <CohortPanel
                        state={subRows[c.id]}
                        drill={child}
                        parent={c}
                        periodWord={periodWord}
                        robotsFor={robotsFor}
                        showRobots={showRobots}
                        depth={depth + 1}
                      />
                    </Td>
                  </Row>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      {state.note && (
        <div style={{ color: MUTED, fontSize: 11, marginTop: 6 }}>
          {state.note}
        </div>
      )}
    </div>
  );
};

// dot fill. Outlier mode: gray + faint for typical schools, the row's tone colour
// for outliers with opacity ramping 0.55 -> 1.0 with distance. Small-district
// fallback: every dot solid in its own tone (no ghost-gray).
const dotAlphaHex = (a) =>
  Math.round(clamp(a, 0, 1) * 255)
    .toString(16)
    .padStart(2, "0");
// `dimmed` is the filter tier, and it sits OUTSIDE the outlier/fallback split
// because it answers a different question: not "how far from normal is this" but
// "is the table still talking about this one".
//
// HOLLOW, not merely paler. A fainter gray fill sits beside the context tier
// (already gray at 0x55) as one level of a gray ramp against another — filter to
// Thriving on a 7-grade district and four excluded discs and two kept-but-typical
// discs read as the same mark. Empty vs filled is
// categorical: whatever a dot's tone, you can see at a glance whether it is still
// in play. Position and radius are untouched, so the spread the plot is claiming
// to show is still every {noun.one}'s.
const dotColor = (s, outlierMode, dimmed) => {
  if (dimmed) return "rgba(0,0,0,0)";
  if (!outlierMode) return markColor(s) + "cc";
  if (!s.isOutlier) return CONTEXT + "55";
  const t = clamp((s.dist - OUTLIER_SIGMA) / 2.5, 0, 1);
  return markColor(s) + dotAlphaHex(0.55 + 0.45 * t);
};


export { FocusTab };
